import React, { useState, useEffect } from 'react';

import clsx from 'clsx';
import {
  Card,
  Divider,
  Typography,
  CardActions,
  colors,
  CardContent,
  TablePagination,
  Button,
} from '@mui/material';
import PropTypes from 'prop-types';
import { useUpdateEffect } from 'react-use';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '@mui/styles';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { get, isEmpty, orderBy, isFunction } from 'lodash';

import { DataTable as DataTableComponent } from '@devias-components';
import useHttp from '@hooks/useHttp';
import {
  LastPage,
  FirstPage,
  KeyboardArrowLeft,
  KeyboardArrowRight,
} from '@mui/icons-material';

const useStyles = makeStyles((theme) => ({
  root: {},
  content: {
    padding: 0,
  },
  inner: {
    minWidth: 700,
  },
  actions: {
    padding: theme.spacing(1),
    justifyContent: 'flex-end',
  },
  actionsContainer: {
    marginBottom: '20px',
  },
  confirmButton: {
    margin: theme.spacing(1),
    marginLeft: theme.spacing(0),
    color: theme.palette.white,
    backgroundColor: colors.green[600],
    '&:hover': {
      backgroundColor: colors.green[900],
    },
  },
  intoValidation: {
    margin: theme.spacing(1),
    marginLeft: theme.spacing(1),
    color: theme.palette.white,
    backgroundColor: colors.orange[600],
    '&:hover': {
      backgroundColor: colors.orange[900],
    },
  },
  rejectButton: {
    color: theme.palette.white,
    backgroundColor: colors.red[600],
    '&:hover': {
      backgroundColor: colors.red[900],
    },
  },
  actionItem: {
    minWidth: '48px',
    height: '48px',
    borderRadius: '50%',
  },
}));

const DataTable = ({
  columns,
  sorting,
  tableId,
  refetch,
  endpoint,
  className,
  onSendData,
  apiCallback,
  initialSortBy,
  isIndeterminateCheckbox,
  handleDataSort,
  initialFilters,
  setSelectedFlatRows,
  handleDataFilter,
  apiConfiguration,
  renderRowSubComponent,
  shouldChangeActionItems = false,
  ...rest
}) => {
  const { t } = useTranslation();
  const { value, loading, reFetch } = useHttp({
    endpoint,
    initialConfig: apiConfiguration,
    cb: apiCallback,
  });

  const [entities, setEntities] = useState([]);
  const [meta, setMeta] = useState({});

  useUpdateEffect(() => {
    reFetch(apiConfiguration);
  }, [apiConfiguration, refetch, tableId]);

  const classes = useStyles();

  useEffect(() => {
    if (!isEmpty(value)) {
      if (!isEmpty(sorting)) {
        setEntities(
          orderBy(
            get(value, 'data'),
            get(sorting, 'fields'),
            get(sorting, 'order'),
          ),
        );
      } else {
        setEntities(get(value, 'data'));
      }
      setMeta(get(value, 'meta'));
    }
  }, [value, sorting]);

  useEffect(() => {
    if (isFunction(onSendData)) {
      onSendData(entities);
    }
  }, [entities, onSendData]);

  const PageButton = ({ page, isDisabled, icon: Icon, customStyle, title }) => (
    <Button
      className={classes.actionItem}
      title={title}
      style={{
        cursor: isDisabled ? 'default' : 'pointer',
        pointerEvents: isDisabled ? 'none' : 'auto',
        color: isDisabled ? 'rgba(0,0,0,0.26)' : 'black',
        ...customStyle,
      }}>
      <Icon
        onClick={() => {
          if (!isDisabled) {
            handlePageChange(page);
          }
        }}
      />
    </Button>
  );

  const getActionItems = (currentPage, lastPage) => {
    return (
      <>
        {shouldChangeActionItems && (
          <PageButton
            page={0}
            isDisabled={currentPage === 0}
            icon={FirstPage}
            customStyle={{ marginLeft: '20px' }}
            title={t('first_page')}
          />
        )}
        <PageButton
          page={currentPage - 1}
          isDisabled={currentPage === 0}
          icon={KeyboardArrowLeft}
          customStyle={!shouldChangeActionItems ? { marginLeft: '20px' } : null}
          title={t('previous_page')}
        />
        <PageButton
          page={currentPage + 1}
          isDisabled={currentPage === lastPage - 1}
          icon={KeyboardArrowRight}
          title={t('next_page')}
        />
        {shouldChangeActionItems && (
          <PageButton
            page={lastPage - 1}
            isDisabled={currentPage === lastPage - 1}
            icon={LastPage}
            title={t('last_page')}
          />
        )}
      </>
    );
  };

  const handlePageChange = (page) => {
    reFetch({
      params: {
        ...apiConfiguration.params,
        page: Number(page) + 1,
      },
    });
  };

  return (
    <div {...rest} className={clsx(classes.root, className)}>
      <Typography color="textSecondary" gutterBottom variant="body2">
        {t('records found', {
          records: meta.total || 0,
          currentPage: meta.current_page,
          lastPage: meta.last_page,
        })}
      </Typography>
      <Card>
        <Divider />
        <CardContent className={classes.content}>
          <PerfectScrollbar>
            <div className={classes.inner}>
              <DataTableComponent
                data={entities}
                columns={columns}
                isIndeterminateCheckbox={isIndeterminateCheckbox}
                isLoading={loading}
                setSelectedFlatRows={setSelectedFlatRows}
                initialSortBy={initialSortBy}
                handleDataSort={handleDataSort}
                initialFilters={initialFilters}
                reFetch={() => reFetch(apiConfiguration)}
                handleDataFilter={handleDataFilter}
                renderRowSubComponent={
                  isFunction(renderRowSubComponent) && renderRowSubComponent
                }
              />
            </div>
          </PerfectScrollbar>
        </CardContent>
        <CardActions className={classes.actions}>
          <TablePagination
            component="div"
            count={meta.total || 0}
            rowsPerPage={+meta.per_page}
            rowsPerPageOptions={[+meta.per_page]}
            labelDisplayedRows={({ from, to, count }) =>
              `${from}-${to} ${t('of')} ${count}`
            }
            page={Math.min(+meta.current_page - 1, Math.max(meta.total - 1, 0))}
            ActionsComponent={() => {
              return getActionItems(
                Math.min(+meta.current_page - 1, Math.max(meta.total - 1, 0)),
                meta.last_page,
              );
            }}
          />
        </CardActions>
      </Card>
    </div>
  );
};

DataTable.defaultProps = {
  tableId: '',
  sorting: {},
  initialSortBy: [],
  initialFilters: [],
};

DataTable.propTypes = {
  tableId: PropTypes.string,
  sorting: PropTypes.object,
  onSendData: PropTypes.func,
  apiCallback: PropTypes.func,
  className: PropTypes.string,
  initialSortBy: PropTypes.array,
  handleDataSort: PropTypes.func,
  initialFilters: PropTypes.array,
  handleDataFilter: PropTypes.func,
  apiConfiguration: PropTypes.object,
  columns: PropTypes.array.isRequired,
  endpoint: PropTypes.func.isRequired,
  renderRowSubComponent: PropTypes.func,
  refetch: PropTypes.string,
};

export default DataTable;
